/**
 * @licence Copyright © 2019 Mercury Redstone BV, all rights reserved
 */
import { useRef, useMemo, ComponentProps } from 'react';
import styled, { css } from 'styled-components';
import { isEmpty } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { useAsync } from 'react-use';
import {
  Table as DefTable,
  TableBody as DefTableBody,
  TableFooter,
  TableHead as DefTableHead,
  TableRow as DefTableRow,
} from '@mui/material';
import { animated } from '@react-spring/web';
import { getPrice } from '../../utils/currency';
import { semiBoldText } from '../../utils/fonts';
import { getPercentages } from '../../utils/percentages';
import {
  minPortfolioItemCostToRender,
  handlePortfolioTableDataItemEmptyIcon,
  isEuroCurrency,
  isStakedCurrency,
} from '../../utils/portfolio';
import { useHorizontalDemoScroll, useIsTablet, useIsMobile } from '../../hooks';
import { PortfolioCurrency, DashboardDataQuery } from '../../apollo';
import { usePortfolioCurrency } from '../../providers';
import { Spinner } from '../../styled';
import {
  OrderCell,
  NameCell,
  PriceCell,
  ChangeCell,
  TokenAmountCell,
  ValueCell,
  PercentageCell,
  TableCell,
  TableRow,
  NameCellText,
  NameCellDesc,
} from '../../styled/portfolio';
import { Text } from '../texts';
import { PortfolioTableRow, PortfolioTableRowData } from './PortfolioTableRow';

export type PrivatePortfolioTableProps = {
  portfolio: DashboardDataQuery['getPrivatePortfolio'];
} & ComponentProps<'div'>;

const PrivatePortfolioTable = ({
  portfolio,
  ...props
}: PrivatePortfolioTableProps) => {
  const { t } = useTranslation();
  const { currency } = usePortfolioCurrency();
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const scroll = useHorizontalDemoScroll(tableWrapperRef);
  const tablet = useIsTablet();
  const mobile = useIsMobile();

  const rawTableData = useMemo(
    () =>
      portfolio.map(
        ({
          cost,
          usdCost,
          currency: { piechartColor: _, ...restCurrency },
          ...rest
        }) =>
          ({
            cost,
            usdCost,
            ...restCurrency,
            ...rest,
          }) as PortfolioTableRowData
      ),
    [portfolio]
  );

  const totalPercentToRender = useMemo(
    () =>
      getPercentages(
        rawTableData.reduce((res, { percentage }) => res + percentage, 0),
        {
          round: true,
        }
      ),
    [rawTableData]
  );

  const {
    loading: loadingTableData,
    error,
    value: tableData = [],
  } = useAsync(() => {
    const dataWithoutIcon = rawTableData
      .filter(
        ({ symbol, cost }) =>
          isEuroCurrency(symbol) || cost >= minPortfolioItemCostToRender
      )
      .filter(({ symbol }) => !isStakedCurrency(symbol));
    return handlePortfolioTableDataItemEmptyIcon(dataWithoutIcon);
  }, [rawTableData]);

  const [fiats, cryptos] = useMemo(
    () =>
      tableData.reduce<[PortfolioTableRowData[], PortfolioTableRowData[]]>(
        (acc, data) =>
          data.isFiat
            ? [[...acc[0], data], acc[1]]
            : [acc[0], [...acc[1], data]],
        [[], []]
      ),
    [tableData]
  );

  const rows = useMemo(
    () =>
      [...fiats, ...cryptos].map((data, index) => (
        <PortfolioTableRow
          key={data.id}
          data={data}
          index={index}
          withFiatIndex
          withTickerLink={false}
        />
      )),
    [cryptos, fiats]
  );

  const stakedTokensData = useMemo(() => {
    const rawData = rawTableData
      .filter(({ symbol }) => isStakedCurrency(symbol))
      .reduce(
        (acc, { cost, usdCost, percentage }) => ({
          amount:
            acc.amount + (currency === PortfolioCurrency.Eur ? cost : usdCost),
          percent: acc.percent + percentage,
        }),
        { amount: 0, percent: 0 }
      );

    return rawData.amount >= 0.01
      ? {
          amount: getPrice(
            rawData.amount,
            currency === PortfolioCurrency.Usd
              ? {
                  currency: 'USD',
                }
              : undefined
          ),
          percent: getPercentages(rawData.percent),
        }
      : null;
  }, [currency, rawTableData]);

  const smallTokensData = useMemo(() => {
    const rawData = rawTableData
      .filter(
        ({ symbol, cost }) =>
          !isEuroCurrency(symbol) && cost < minPortfolioItemCostToRender
      )
      .reduce(
        (acc, { cost, usdCost, percentage }) => ({
          amount:
            acc.amount + (currency === PortfolioCurrency.Eur ? cost : usdCost),
          percent: acc.percent + percentage,
        }),
        { amount: 0, percent: 0 }
      );

    return rawData.amount >= 0.01
      ? {
          amount: getPrice(
            rawData.amount,
            currency === PortfolioCurrency.Usd
              ? {
                  currency: 'USD',
                }
              : undefined
          ),
          percent: getPercentages(rawData.percent),
        }
      : null;
  }, [currency, rawTableData]);

  const portfolioTotalCost = useMemo(() => {
    const costAmount = (portfolio || [])
      .map(({ cost, usdCost }) =>
        currency === PortfolioCurrency.Eur ? cost : usdCost
      )
      .reduce((res, cost) => res + cost, 0);

    return getPrice(
      costAmount,
      currency !== PortfolioCurrency.Eur
        ? {
            currency: 'USD',
          }
        : undefined
    );
  }, [portfolio, currency]);

  const [stakedNameColSpan, stakedExtraColSpan] = useMemo(() => {
    switch (true) {
      case mobile:
        return [3, 1] as const;
      case tablet:
        return [3, 1] as const;
      default:
        return [4, 0] as const;
    }
  }, [mobile, tablet]);

  return (
    <Wrapper
      ref={tableWrapperRef}
      scrollLeft={scroll}
      $loading={loadingTableData}
      {...props}
    >
      {(() => {
        if (loadingTableData && isEmpty(tableData)) {
          return (
            <Spinner
              style={{
                margin: 'auto',
              }}
            />
          );
        }

        if (error) {
          return (
            <Text
              style={{
                width: '100%',
              }}
              align={'center'}
            >
              Error
            </Text>
          );
        }

        return (
          <Table>
            <TableHead>
              <DefTableRow>
                <OrderCell />
                <NameCell>{t('DASHBOARD_PAGE__portfolioNameColumn')}</NameCell>
                <PriceCell>
                  {t('DASHBOARD_PAGE__portfolioPriceColumn')}
                </PriceCell>
                <ChangeCell>
                  {t('DASHBOARD_PAGE__portfolioChangeColumn')}
                </ChangeCell>
                <TokenAmountCell>
                  {t('DASHBOARD_PAGE__portfolioCoinsColumn')}
                </TokenAmountCell>
                <ValueCell>
                  {t('DASHBOARD_PAGE__portfolioValueColumn')}
                </ValueCell>
                <PercentageCell>
                  {t('DASHBOARD_PAGE__portfolioPercentageColumn')}
                </PercentageCell>
              </DefTableRow>
            </TableHead>
            <TableBody>
              {rows}
              {!!stakedTokensData && (
                <TableRow>
                  <OrderCell />
                  <NameCell colSpan={stakedNameColSpan}>
                    <NameCellText $noWrap={false}>
                      {t('DASHBOARD_PAGE__portfolioStakedTokensSumTitleTop')}
                      <br />
                      <NameCellDesc>
                        {t(
                          'DASHBOARD_PAGE__portfolioStakedTokensSumTitleBottom'
                        )}
                      </NameCellDesc>
                    </NameCellText>
                  </NameCell>
                  {!!stakedExtraColSpan && (
                    <TableCell colSpan={stakedExtraColSpan} />
                  )}
                  <ValueCell>{stakedTokensData.amount}</ValueCell>
                  <PercentageCell>{stakedTokensData.percent}</PercentageCell>
                </TableRow>
              )}
              {!!smallTokensData && (
                <TableRow>
                  <OrderCell />
                  <NameCell $lineHeight={1} colSpan={2}>
                    {t('DASHBOARD_PAGE__portfolioSmallTokensSumTitle')}
                  </NameCell>
                  <TableCell colSpan={2} />
                  <ValueCell>{smallTokensData.amount}</ValueCell>
                  <PercentageCell>{smallTokensData.percent}</PercentageCell>
                </TableRow>
              )}
            </TableBody>
            <TableFooter>
              <DefTableRow>
                <OrderCell />
                <NameCell>{t('DASHBOARD_PAGE__portfolioTotal')}</NameCell>
                <TableCell colSpan={3} />
                <ValueCell>{portfolioTotalCost}</ValueCell>
                <PercentageCell>{totalPercentToRender}</PercentageCell>
              </DefTableRow>
            </TableFooter>
          </Table>
        );
      })()}
    </Wrapper>
  );
};

const Wrapper = styled(animated.div)<{
  $loading: boolean;
}>`
  display: flex;
  overflow-x: auto;
  ${({ $loading }) =>
    $loading &&
    css`
      min-height: 60px;
    `}
`;

const Table = styled(DefTable)`
  min-width: 100%;
  width: auto;
  flex: 1 1 auto;
  table-layout: fixed;

  /*noinspection CssUnusedSymbol*/
  .MuiTableCell-root {
    border-bottom: none;
  }

  /*noinspection CssUnusedSymbol*/
  .MuiTableCell-footer {
    ${semiBoldText};
  }
`;

const TableHead = styled(DefTableHead)`
  /*noinspection CssUnusedSymbol*/
  .MuiTableCell-head {
    padding-top: 14px;
    padding-bottom: 14px;
    border-bottom: none;
    ${semiBoldText};
  }
`;

const TableBody = styled(DefTableBody)``;

export { PrivatePortfolioTable };
